import { useEffect, useState } from 'react';
import { StatusChip } from './legalShared';
import { API_URL } from '../../config/api';
import { readApiJson } from '../../utils/apiErrors';

function fmtWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function SparkleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3.5 13.9 8.6 19 10.5l-5.1 1.9L12 17.5l-1.9-5.1L5 10.5l5.1-1.9L12 3.5Z" fill="currentColor" />
      <path d="M18.5 14.5 19.2 16.3 21 17l-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7.7-1.8Z" fill="currentColor" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M20 11a8 8 0 1 0-2.2 6.4M20 5v6h-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronIcon({ collapsed }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={`legal-ai-sec-chevron${collapsed ? ' collapsed' : ''}`}>
      <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Collapsible card for one summary section — same white "insight card" shell
// (.legal-ic) the Legal Insights tab already uses everywhere else on this
// dashboard, just with a clickable header that expands/collapses its body,
// so this tab reads as a native part of Legal rather than a dropped-in chat
// widget.
function AISection({ icon, title, count, collapsed, onToggle, className = '', children }) {
  return (
    <div className={`legal-ic legal-ai-sec${className ? ` ${className}` : ''}`}>
      <button type="button" className="legal-ai-sec-head" onClick={onToggle} aria-expanded={!collapsed}>
        <span className="legal-ic-title">
          {icon} {title}
          {typeof count === 'number' && <span className="legal-ai-sec-count">{count}</span>}
        </span>
        <ChevronIcon collapsed={collapsed} />
      </button>
      {!collapsed && <div className="legal-ai-sec-body">{children}</div>}
    </div>
  );
}

const DOC_LIST_LIMIT = 8;

function DocList({ docs, dateField, emptyText }) {
  if (!docs.length) {
    return <div className="legal-ai-list-empty">{emptyText}</div>;
  }
  return (
    <div className="legal-ai-list">
      {docs.slice(0, DOC_LIST_LIMIT).map(doc => (
        <div className="legal-ai-list-row" key={doc.id}>
          <div className="legal-ai-list-main">
            <strong>{doc.entity_name || 'Unnamed entity'}</strong>
            <span>{doc.document_category || '—'}{doc.document_subcategory ? ` · ${doc.document_subcategory}` : ''}</span>
          </div>
          <div className="legal-ai-list-side">
            <StatusChip value={doc.current_status || 'Unspecified'} />
            {doc[dateField] && <span className="legal-ai-list-date">{fmtWhen(doc[dateField])}</span>}
          </div>
        </div>
      ))}
      {docs.length > DOC_LIST_LIMIT && (
        <div className="legal-ai-list-more">+ {docs.length - DOC_LIST_LIMIT} more</div>
      )}
    </div>
  );
}

// Fetch/state for the "since your last login" summary — `autoLoad` fires the
// first fetch once it flips true; the tab below passes `true` outright, so
// it loads the moment it mounts.
function useAISummary(autoLoad) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/legal/ai-summary/`, { credentials: 'include' });
      const body = await readApiJson(res, 'Could not load the AI summary.');
      setResult(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  };

  useEffect(() => {
    if (autoLoad && !loadedOnce) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad]);

  return { loading, error, result, load };
}

// ── LegalAISummaryTab ────────────────────────────────────────────────────────
// The "since your last login" canned summary, as its own "AI Legal Summary"
// sub-tab — always visible (and auto-loaded on mount) once that tab is
// selected, full dashboard width, built from the same hero-tile/insight-card
// pieces (legal-hero-row, legal-ic, legal-ov-grid) every other Legal tab
// already uses, instead of the cramped single-column chat-popup look this
// used to borrow. The free-form question-answering circle is a separate
// thing now — DashboardAIChat, wired up in LegalMain.js — so this tab is
// purely the at-a-glance "what changed since I was last here" summary.
function LegalAISummaryTab() {
  const { loading, error, result, load } = useAISummary(true);
  const [collapsedSections, setCollapsedSections] = useState({});
  const toggleSection = (key) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  const sectionKeys = ['newDocs', 'statusChanges', 'completion'];
  const allCollapsed = sectionKeys.every(key => collapsedSections[key]);
  const toggleAll = () => {
    const next = !allCollapsed;
    setCollapsedSections(Object.fromEntries(sectionKeys.map(key => [key, next])));
  };

  const hasContent = result && result.has_previous_login && result.completion;
  const delta = hasContent ? result.completion.completed_pct_now - result.completion.completed_pct_at_last_login : 0;
  const completionTone = delta > 0 ? 'legal-hero-green' : delta < 0 ? 'legal-hero-red' : 'legal-hero-slate';
  const completionArrow = delta > 0 ? '▲' : delta < 0 ? '▼' : '—';

  return (
    <div className="legal-ai-tab">
      <div className="legal-ai-tab-head">
        <div className="legal-ai-tab-head-icon"><SparkleIcon /></div>
        <div className="legal-ai-tab-head-text">
          <div className="legal-ai-tab-title">Legal AI Summary</div>
          <div className="legal-ai-tab-subtitle">
            <span className="ai-status" aria-hidden="true" />
            {result?.previous_login ? `Since ${fmtWhen(result.previous_login)}` : 'Updates since your last login'}
          </div>
        </div>
        <button className="legal-ai-tab-refresh" type="button" onClick={load} disabled={loading} title="Refresh">
          <RefreshIcon />
          <span>Refresh</span>
        </button>
      </div>

      {loading && (
        <div className="legal-ai-tab-loading">
          <span className="legal-ai-tab-loading-icon"><SparkleIcon /></span>
          Analyzing changes since your last login…
        </div>
      )}

      {!loading && error && (
        <div className="legal-ai-tab-empty">
          <div className="legal-ai-tab-empty-title">Couldn&rsquo;t load summary</div>
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && result && !result.has_previous_login && (
        <div className="legal-ai-tab-empty">
          <div className="legal-ai-tab-empty-icon"><SparkleIcon /></div>
          <div className="legal-ai-tab-empty-title">Welcome!</div>
          <p>{result.summary}</p>
        </div>
      )}

      {!loading && !error && result && result.has_previous_login && !result.completion && (
        <div className="legal-ai-tab-empty"><p>{result.summary}</p></div>
      )}

      {!loading && !error && hasContent && (
        <>
          <div className="legal-hero-row">
            <div className="legal-hero-tile legal-hero-violet">
              <span className="legal-hero-icon">🆕</span>
              <div className="legal-hero-body">
                <div className="legal-hero-label">New Docs</div>
                <div className="legal-hero-val">{result.new_documents.length}</div>
                <div className="legal-hero-sub">since last login</div>
              </div>
            </div>
            <div className="legal-hero-tile legal-hero-cyan">
              <span className="legal-hero-icon">🔄</span>
              <div className="legal-hero-body">
                <div className="legal-hero-label">Status Changes</div>
                <div className="legal-hero-val">{result.updated_documents.length}</div>
                <div className="legal-hero-sub">since last login</div>
              </div>
            </div>
            <div className={`legal-hero-tile ${completionTone}`}>
              <span className="legal-hero-icon">{completionArrow}</span>
              <div className="legal-hero-body">
                <div className="legal-hero-label">Completion</div>
                <div className="legal-hero-val">{result.completion.completed_pct_now}%</div>
                <div className="legal-hero-sub">
                  was {result.completion.completed_pct_at_last_login}% ({result.completion.completed_at_last_login}/{result.completion.total_at_last_login})
                </div>
              </div>
            </div>
          </div>

          <button type="button" className="legal-ai-toggle-all" onClick={toggleAll}>
            {allCollapsed ? '▾ Expand all sections' : '▴ Collapse all sections'}
          </button>

          <div className="legal-ic legal-ai-narrative">
            <div className="legal-ic-title">📝 AI Narrative</div>
            <div className="legal-ai-narrative-body">
              {result.summary.split('\n\n').map((para, i) => <p key={i}>{para}</p>)}
            </div>
            {!result.ai_generated && (
              <div className="legal-ai-fallback">Generated from raw change counts — AI narration unavailable right now.</div>
            )}
          </div>

          <div className="legal-ov-grid">
            <AISection
              icon="🆕" title="New Documents Initiated by Legal"
              count={result.new_documents.length}
              collapsed={!!collapsedSections.newDocs}
              onToggle={() => toggleSection('newDocs')}
            >
              <DocList docs={result.new_documents} dateField="create_date" emptyText="No new documents were logged since your last login." />
            </AISection>

            <AISection
              icon="🔄" title="Status Changes on Existing Documents"
              count={result.updated_documents.length}
              collapsed={!!collapsedSections.statusChanges}
              onToggle={() => toggleSection('statusChanges')}
            >
              <DocList docs={result.updated_documents} dateField="last_update_date" emptyText="No existing documents changed status since your last login." />
            </AISection>
          </div>

          <AISection
            icon="📊" title="Overall Completion Change" className="legal-ic-full-row"
            collapsed={!!collapsedSections.completion}
            onToggle={() => toggleSection('completion')}
          >
            <div className="legal-ai-completion-row">
              <div className="legal-ai-completion-block">
                <span>At last login</span>
                <strong>{result.completion.completed_pct_at_last_login}%</strong>
                <div className="legal-ai-progress"><div className="legal-ai-progress-fill" style={{ width: `${result.completion.completed_pct_at_last_login}%` }} /></div>
                <em>{result.completion.completed_at_last_login} of {result.completion.total_at_last_login} completed</em>
              </div>
              <div className="legal-ai-completion-arrow">→</div>
              <div className="legal-ai-completion-block legal-ai-completion-block-now">
                <span>Now</span>
                <strong>{result.completion.completed_pct_now}%</strong>
                <div className="legal-ai-progress"><div className="legal-ai-progress-fill" style={{ width: `${result.completion.completed_pct_now}%` }} /></div>
                <em>{result.completion.completed_now} of {result.completion.total_now} completed</em>
              </div>
            </div>
          </AISection>
        </>
      )}
    </div>
  );
}

export { LegalAISummaryTab };
export default LegalAISummaryTab;
